// Shared helpers of the data import wizard (paste, parse and template download).

import { neutralizeCsvCell } from "@/utils/sanitize/escape";

// Tipo para errores de validación
export type ValidationError = {
  row: number;
  field: string;
  error: string;
};

// Labels of the three instruction rows (and their first column) of the downloaded template.
export const columnDescription = {
  header: {
    en: "Header (do not remove or modify this column and row) =>",
    es: "Encabezado (no quitar o modificar esta columna y fila) =>",
  },
  info: { en: "Info =>", es: "Info =>" },
  sampleData: { en: "Sample Data =>", es: "Datos ejemplo =>" },
};

/**
 * Column separator of the pasted text for the selected paste format. The grid, the
 * column count check and the validation must all use this one, or a format splits
 * differently in each place.
 */
export const getPasteSeparator = (format: string): string => {
  if (format === "csv-comma") return ",";
  if (format === "csv-semicolon") return ";";
  return "\t";
};

/**
 * Splits delimited text into rows of cells following the usual CSV quoting rules,
 * which Excel also applies to what it puts on the clipboard:
 *  - a cell may be wrapped in double quotes, and then it can hold the separator and
 *    line breaks;
 *  - inside a quoted cell, two double quotes stand for one;
 *  - a double quote in the middle of an unquoted cell is kept as it is.
 * Rows may end in \n, \r\n or \r. Rows with nothing in them are dropped.
 */
export const parseDelimitedText = (text: string, separator: string): string[][] => {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let inQuotes = false;
  let cellStart = true;

  const endCell = () => {
    row.push(cell);
    cell = "";
    cellStart = true;
  };
  const endRow = () => {
    endCell();
    if (row.some((value) => value.trim() !== "")) rows.push(row);
    row = [];
  };

  for (let i = 0; i < text.length; i++) {
    const char = text[i];

    if (inQuotes) {
      if (char === '"') {
        if (text[i + 1] === '"') {
          cell += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        cell += char;
      }
      continue;
    }

    if (char === '"' && cellStart) {
      inQuotes = true;
      cellStart = false;
    } else if (char === separator) {
      endCell();
    } else if (char === "\n" || char === "\r") {
      if (char === "\r" && text[i + 1] === "\n") i++;
      endRow();
    } else {
      cell += char;
      cellStart = false;
    }
  }
  // The last row has no line break after it.
  if (cell !== "" || row.length > 0) endRow();

  return rows;
};

/**
 * The rows of what was pasted in the selected format. When the whole sheet of the
 * downloaded template was pasted, its instruction rows and the label column are
 * left out, so only the data remains.
 */
export const parsePastedRows = (text: string, format: string): string[][] => {
  const rows = parseDelimitedText(text, getPasteSeparator(format));

  const labels = new Set(
    [columnDescription.header, columnDescription.info, columnDescription.sampleData].flatMap((label) =>
      Object.values(label).map((value) => value.trim()),
    ),
  );
  const isLabelRow = (row: string[]) => labels.has((row[0] ?? "").trim());
  if (!rows.some(isLabelRow)) return rows;

  return rows.filter((row) => !isLabelRow(row)).map((row) => row.slice(1));
};

/**
 * Builds delimited text (the inverse of parseDelimitedText): cells that hold the
 * separator, a double quote or a line break are quoted, with the quotes doubled.
 */
export const toDelimitedText = (rows: string[][], separator: string): string =>
  rows
    .map((row) =>
      row
        .map((rawCell) => {
          // A cell that would run as a formula in Excel gets a leading quote.
          const cell = neutralizeCsvCell(rawCell);
          return cell.includes(separator) || /["\r\n]/.test(cell) ? `"${cell.replace(/"/g, '""')}"` : cell;
        })
        .join(separator),
    )
    .join("\r\n");


/**
 * The template as an .xlsx file: the same rows as the CSV, with the header row in
 * bold, the info row in grey and every cell kept as text, so Excel does not turn a
 * date or a phone number into something else. `numberColumns` are the positions of
 * the columns that hold numbers, written as such. The first column holds the labels
 * of the instruction rows and is left out when the sheet is pasted back.
 */
export const buildTemplateWorkbook = async (
  rows: string[][],
  options: { sheetName: string; numberColumns?: number[] },
): Promise<Blob> => {
  // Loaded on demand: only a download in Excel format needs the library.
  const { default: writeExcelFile } = await import("write-excel-file/universal");
  const numberColumns = new Set(options.numberColumns ?? []);

  const sheetData = rows.map((row, rowIndex) =>
    row.map((cell, columnIndex) => {
      if (rowIndex === 0) {
        return {
          value: cell,
          type: String,
          format: "@",
          fontWeight: "bold" as const,
          backgroundColor: "#FDE9C8",
          wrap: true,
          alignVertical: "center" as const,
        };
      }
      if (rowIndex === 1) {
        return {
          value: cell,
          type: String,
          format: "@",
          fontStyle: "italic" as const,
          textColor: "#666666",
          backgroundColor: "#F3F4F6",
          wrap: true,
          alignVertical: "top" as const,
        };
      }
      if (cell === "") return null;
      if (numberColumns.has(columnIndex) && cell.trim() !== "" && !Number.isNaN(Number(cell))) {
        return { value: Number(cell), type: Number };
      }
      return { value: cell, type: String, format: "@" };
    }),
  );

  const columnCount = Math.max(0, ...rows.map((row) => row.length));
  const columns = Array.from({ length: columnCount }, (_, columnIndex) => {
    // Header and info rows wrap, so their width follows the data and is bounded.
    const longest = Math.max(
      ...rows.slice(2).map((row) => (row[columnIndex] ?? "").length),
      (rows[0]?.[columnIndex] ?? "").length / 2,
    );
    return { width: Math.min(40, Math.max(16, Math.ceil(longest) + 2)) };
  });

  return writeExcelFile(sheetData, {
    sheet: options.sheetName,
    columns,
    stickyRowsCount: 2,
    stickyColumnsCount: 1,
  }).toBlob();
};
